import { BRAND_COPY } from '@fyf/shared';
import { useEffect, useRef, useState } from 'react';

import { getSubmission } from '../api/client.js';
import { ScreenLayout } from '../components/ScreenLayout.js';
import { SparkleAnimation } from '../components/SparkleAnimation.js';
import { useCaptureDispatch, useCaptureState } from '../context/CaptureFlowContext.js';

const { generating } = BRAND_COPY.screens;
const POLL_INTERVAL = 2000;
const FALLBACK_DELAY = 15000;

export function GeneratingScreen() {
  const { submissionId } = useCaptureState();
  const dispatch = useCaptureDispatch();
  const [showFallback, setShowFallback] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const fallbackRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!submissionId) return;

    fallbackRef.current = setTimeout(() => setShowFallback(true), FALLBACK_DELAY);

    pollRef.current = setInterval(async () => {
      try {
        const submission = await getSubmission(submissionId);
        if (submission.status === 'complete' && submission.flameImageUrl) {
          dispatch({ type: 'SET_FLAME_IMAGE_URL', payload: submission.flameImageUrl });
          if (submission.surfaceName) {
            dispatch({ type: 'SET_SURFACE_NAME', payload: submission.surfaceName });
          }
          if (submission.surfaceEventHashtag) {
            dispatch({ type: 'SET_EVENT_HASHTAG', payload: submission.surfaceEventHashtag });
          }
          dispatch({ type: 'SET_SCREEN', payload: 'reveal' });
        } else if (submission.status === 'failed') {
          dispatch({ type: 'SET_ERROR', payload: 'Flame generation failed. Please try again.' });
          dispatch({ type: 'SET_SCREEN', payload: 'reveal' });
        }
      } catch {
        // Silently retry on network errors
      }
    }, POLL_INTERVAL);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (fallbackRef.current) clearTimeout(fallbackRef.current);
    };
  }, [submissionId, dispatch]);

  return (
    <ScreenLayout>
      <div className="flex flex-col items-center" role="status" aria-live="polite">
        <SparkleAnimation />
        <h2 className="text-[32px] font-['Lora',serif] text-center mb-4">
          Igniting <span className="text-brand-gradient italic">possibility</span>
        </h2>
        <p className="font-['DM_Sans',sans-serif] text-base text-white text-center max-w-md mb-6">
          {generating.subtext}
        </p>
        <p className="font-['DM_Sans',sans-serif] text-base text-white text-center max-w-md mb-4">
          {generating.brandMessage}
        </p>
        {showFallback && (
          <p className="text-white text-center text-sm">{generating.fallbackText}</p>
        )}
      </div>

      <img src="/aduro-logo-red.svg" alt="Aduro" className="mt-10 h-9 opacity-80" />
    </ScreenLayout>
  );
}
